import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    StyledButton,
    StyledAnchor,
    Spinner,
    StyledTextButton,
    StyledTextAnchor,
} from './Button.styles';
import { VisuallyHidden } from '../OnboardingLayout/OnboardingLayout.styles';

/**
 * Pill button (52px). `href` renders an anchor; external links get the
 * "(se abre en una pestaña nueva)" hint for screen readers.
 */
export const Button = ({
    variant = 'primary',
    iconLeft,
    iconRight,
    loading = false,
    loadingLabel,
    disabled = false,
    href,
    external = false,
    type = 'button',
    children,
    ...rest
}) => {
    const { t } = useTranslation();
    const content = loading ? (
        <>
            <Spinner className="ri-loader-4-line" aria-hidden="true" />
            <span>{loadingLabel || children}</span>
        </>
    ) : (
        <>
            {iconLeft && <i className={iconLeft} aria-hidden="true" />}
            <span>{children}</span>
            {iconRight && <i className={iconRight} aria-hidden="true" />}
            {external && <VisuallyHidden>{t('SignUpV2.common.newTab')}</VisuallyHidden>}
        </>
    );

    if (href && !disabled) {
        return (
            <StyledAnchor
                variant={variant}
                href={href}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                {...rest}
            >
                {content}
            </StyledAnchor>
        );
    }

    return (
        <StyledButton
            variant={variant}
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...rest}
        >
            {content}
        </StyledButton>
    );
};

/** Text link-style button (min 44px tall). */
export const TextButton = ({ tone = 'default', icon, iconRight, href, children, ...rest }) => {
    const content = (
        <>
            {icon && <i className={icon} aria-hidden="true" />}
            <span>{children}</span>
            {iconRight && <i className={iconRight} aria-hidden="true" />}
        </>
    );

    if (href) {
        return (
            <StyledTextAnchor tone={tone} href={href} {...rest}>
                {content}
            </StyledTextAnchor>
        );
    }

    return (
        <StyledTextButton tone={tone} type="button" {...rest}>
            {content}
        </StyledTextButton>
    );
};

export default Button;
